/**
 * The units the body step offers, and what a figure becomes when one is
 * swapped for another.
 *
 * Swapping the unit beside a filled box converts what is in it. The
 * alternative — leaving `168` in place and calling it feet — is not a
 * cosmetic bug: it is a different person, and everything a plan is
 * calculated off is downstream of it.
 */

import type { HeightUnit, WeightUnit } from "./types/preconsultation.types";

export const HEIGHT_UNITS: readonly HeightUnit[] = ["cm", "ft"];
export const WEIGHT_UNITS: readonly WeightUnit[] = ["kg", "lb"];

const CM_PER_FT = 30.48;
const LB_PER_KG = 2.2046226218;

/**
 * How many decimals a figure is worth keeping in each unit — roughly what the
 * unit is read at, so a converted height reads like a height rather than like
 * the output of a division.
 *
 * Feet get two because one is too coarse to survive the round trip: a tenth of
 * a foot is three centimetres, so `170` would come back as `171`. At two, cm →
 * ft → cm lands on the number that was typed. Kilos and pounds are read to the
 * tenth on any scale, and a tenth of a kilo is worth more than a tenth of a
 * pound, so a pound rounded and converted twice can settle a tenth away from
 * where it started. That is the drift a conversion has, not a bug in one.
 */
const DECIMALS: Record<HeightUnit | WeightUnit, number> = {
  cm: 0,
  ft: 2,
  kg: 1,
  lb: 1,
};

/**
 * `167.9448` as `168`, `5.5000` as `5.5`.
 *
 * Trailing zeros go because the box is typed in as well as written to, and
 * `68.0` is not what anybody types when they mean sixty-eight.
 */
const trim = (figure: number, unit: HeightUnit | WeightUnit): string =>
  `${Number(figure.toFixed(DECIMALS[unit]))}`;

/**
 * Converts what is in a box, or hands it back untouched.
 *
 * Untouched covers the two cases worth being careful about: the unit that did
 * not actually change, and a box holding `""` or a half-typed `5.` while
 * somebody is still in it. Neither is a number to convert, and clearing a
 * field somebody is typing in is worse than leaving it alone.
 */
const convert = <Unit extends HeightUnit | WeightUnit>(
  value: string,
  from: Unit,
  to: Unit,
  perFrom: (figure: number) => number
): string => {
  if (from === to) return value;

  const figure = Number(value.trim());
  if (!value.trim() || !Number.isFinite(figure)) return value;

  return trim(perFrom(figure), to);
};

/** A height, moved between centimetres and feet. */
export const convertHeight = (
  value: string,
  from: HeightUnit,
  to: HeightUnit
): string =>
  convert(value, from, to, (figure) =>
    to === "cm" ? figure * CM_PER_FT : figure / CM_PER_FT
  );

/** A weight, moved between kilos and pounds. */
export const convertWeight = (
  value: string,
  from: WeightUnit,
  to: WeightUnit
): string =>
  convert(value, from, to, (figure) =>
    to === "kg" ? figure / LB_PER_KG : figure * LB_PER_KG
  );

/**
 * A height in centimetres, whatever unit it was typed in.
 *
 * The box offers feet because that is how a height is said here; the record
 * keeps centimetres, so that every height on it can be compared with every
 * other without each one carrying the question of which unit it is in. The
 * conversion happens once, at the wire — see `toPreconsultationPayload`.
 */
export const heightInCm = (value: string, unit: HeightUnit): string =>
  convertHeight(value, unit, "cm");
